/**
 * OIDC (Authorization Code + PKCE) against Zitadel.
 *
 * The dashboard is a public client — no secret. Sessions renew silently via a
 * refresh token (offline_access scope), so no hidden iframe is needed. The full
 * access token is a Zitadel JWT that @prismtx/control-plane verifies via JWKS.
 */

import { UserManager, WebStorageStateStore, Log, type User } from 'oidc-client-ts';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { config } from './config';

// Quiet in normal use; flip to Log.DEBUG when chasing an OIDC problem.
Log.setLogger(console);
Log.setLevel(Log.WARN);

export const userManager = new UserManager({
  authority: config.oidc.authority,
  client_id: config.oidc.clientId,
  redirect_uri: config.oidc.redirectUri,
  post_logout_redirect_uri: config.oidc.postLogoutRedirectUri,
  response_type: 'code',
  scope: config.oidc.scope,
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

/** Current, non-expired access token — or null if we need to (re-)authenticate. */
export async function currentAccessToken(): Promise<string | null> {
  const user = await userManager.getUser();
  if (!user || user.expired) return null;
  return user.access_token ?? null;
}

type Status = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthValue {
  user: User | null;
  status: Status;
  error?: string;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string>();
  const started = useRef(false);

  useEffect(() => {
    // React 18/19 strict mode double-invokes effects; init exactly once.
    if (started.current) return;
    started.current = true;

    const onLoaded = (u: User) => {
      setUser(u);
      setStatus('authenticated');
    };
    const onUnloaded = () => {
      setUser(null);
      setStatus('unauthenticated');
    };
    const onRenewError = (e: Error) => setError(e.message);

    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addSilentRenewError(onRenewError);

    const atCallback = window.location.pathname === config.oidc.redirectPath;
    void (async () => {
      try {
        if (atCallback) {
          const u = await userManager.signinRedirectCallback();
          window.history.replaceState({}, '', '/');
          setUser(u);
          setStatus('authenticated');
          return;
        }
        const u = await userManager.getUser();
        if (u && !u.expired) {
          setUser(u);
          setStatus('authenticated');
        } else {
          setStatus('unauthenticated');
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setStatus('error');
        // Don't leave a spent ?code=&state= stuck in the address bar.
        if (atCallback) {
          window.history.replaceState({}, '', '/');
        }
      }
    })();

    return () => {
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeSilentRenewError(onRenewError);
    };
  }, []);

  const login = () => void userManager.signinRedirect();
  const logout = () => void userManager.signoutRedirect();

  return (
    <AuthContext.Provider value={{ user, status, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
